import { useEffect } from "react";
import { useI18n } from "../i18n/i18n";
import { useSeo } from "../hooks/useSeo";

function Section({ id, heading, children }) {
  return (
    <section id={id} className="mt-12">
      <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight mb-5">
        {heading}
      </h2>
      <div className="space-y-5 text-[15px] text-slate-300 leading-[1.85]">{children}</div>
    </section>
  );
}

function SubBlock({ heading, body }) {
  return (
    <div>
      <h3 className="font-display text-lg text-white mb-2">{heading}</h3>
      <p className="whitespace-pre-line">{body}</p>
    </div>
  );
}

function MultiLineBlock({ heading, body }) {
  return (
    <div>
      <h3 className="font-display text-lg text-white mb-2">{heading}</h3>
      <div className="space-y-3">
        {body.split("\n").map((line, i) =>
          line.startsWith("- ") ? (
            <p key={i} className="pl-4 flex items-start gap-3">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-sky-400 shrink-0" />
              <span>{line.slice(2)}</span>
            </p>
          ) : (
            <p key={i}>{line}</p>
          )
        )}
      </div>
    </div>
  );
}

export default function Privacy() {
  const { t, lang } = useI18n();

  useSeo({
    title: lang === "de" ? "Datenschutzerklärung" : "Privacy Policy",
    description:
      lang === "de"
        ? "Datenschutzerklärung von Cloudship — Hinweise gemäß DSGVO und BDSG."
        : "Cloudship privacy policy — information pursuant to GDPR / DSGVO and the German Federal Data Protection Act.",
    path: "/datenschutz",
    lang,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const processors = t("privacy.processors.items");

  return (
    <main data-testid="privacy-page" className="pt-32 pb-24">
      <section className="max-w-3xl mx-auto px-6 md:px-8">
        <p className="cs-overline mb-4">Legal</p>
        <h1 className="font-display text-4xl sm:text-5xl text-white leading-tight">
          {t("privacy.title")}
        </h1>
        <p className="text-slate-400 mt-4 leading-relaxed">{t("privacy.intro")}</p>

        <Section id="glance" heading={t("privacy.glance.heading")}>
          <SubBlock heading={t("privacy.glance.general_h")} body={t("privacy.glance.general_p")} />
          <SubBlock heading={t("privacy.glance.controller_h")} body={t("privacy.glance.controller_p")} />
          <SubBlock heading={t("privacy.glance.how_h")} body={t("privacy.glance.how_p")} />
          <SubBlock heading={t("privacy.glance.why_h")} body={t("privacy.glance.why_p")} />
          <SubBlock heading={t("privacy.glance.rights_h")} body={t("privacy.glance.rights_p")} />
        </Section>

        <Section id="controller" heading={t("privacy.controller.heading")}>
          <p className="whitespace-pre-line">{t("privacy.controller.body")}</p>
        </Section>

        <Section id="general" heading={t("privacy.general.heading")}>
          <SubBlock heading={t("privacy.general.privacy_h")} body={t("privacy.general.privacy_p")} />
          <SubBlock heading={t("privacy.general.revoke_h")} body={t("privacy.general.revoke_p")} />
          <SubBlock heading={t("privacy.general.complaint_h")} body={t("privacy.general.complaint_p")} />
          <SubBlock heading={t("privacy.general.portability_h")} body={t("privacy.general.portability_p")} />
          <SubBlock heading={t("privacy.general.ssl_h")} body={t("privacy.general.ssl_p")} />
          <SubBlock heading={t("privacy.general.ads_h")} body={t("privacy.general.ads_p")} />
        </Section>

        <Section id="collection" heading={t("privacy.collection.heading")}>
          <SubBlock heading={t("privacy.collection.cookies_h")} body={t("privacy.collection.cookies_p")} />
          <MultiLineBlock heading={t("privacy.collection.logs_h")} body={t("privacy.collection.logs_p")} />
          <SubBlock heading={t("privacy.collection.form_h")} body={t("privacy.collection.form_p")} />
        </Section>

        <Section id="processors" heading={t("privacy.processors.heading")}>
          <p>{t("privacy.processors.intro")}</p>
          <ul className="space-y-5 mt-3">
            {processors.map((p, i) => (
              <li key={i} className="cs-card p-5">
                <p className="font-display text-white text-base">{p.name}</p>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{p.desc}</p>
              </li>
            ))}
          </ul>
        </Section>

        <p className="text-xs text-slate-500 mt-14">
          {t("privacy.source_label")}:{" "}
          <a
            href={t("privacy.source_value")}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-400 hover:text-sky-300"
          >
            {t("privacy.source_value")}
          </a>
        </p>
      </section>
    </main>
  );
}
